import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { approveUpdate, getUpdateRequests, rejectUpdate } from "../../../api/apiCall";
import { useAuth } from "../../../auth/auth";
import { format } from "date-fns";
import {
  TextField,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  IconButton,
  Paper,
  Divider,
  Stack,
  useMediaQuery,
  useTheme,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import ScheduleIcon from "@mui/icons-material/Schedule";

const PendingUpdates = () => {
  const { state } = useAuth();
  const [workers, setWorkers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<number | null>(0);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const fetchPendingUpdates = async () => {
    try {
      setLoading(true);
      const response: any = await getUpdateRequests(state.user?.token, statusFilter);
      console.log(response);
      setWorkers(response.data.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingUpdates();
  }, [statusFilter]);

  const handleRowClick = (params: any) => {
    setSelectedRequest(params.row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRequest(null);
  };

  const handleCloseReasonModal = () => {
    setReasonModalOpen(false);
    setReason("");
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      await approveUpdate(state.user?.token, selectedRequest.id);
      await fetchPendingUpdates();
      handleCloseModal();
    } catch (error) {
      console.error("Approval failed:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      await rejectUpdate(state.user?.token, selectedRequest.id, reason);
      await fetchPendingUpdates();
      handleCloseModal();
      handleCloseReasonModal();
    } catch (error) {
      console.error("Rejection failed:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const getParsedChanges = (changesString: string) => {
    try {
      return JSON.parse(changesString);
    } catch (e) {
      console.error("Failed to parse requestedChanges", e);
      return { error: "Unable to parse changes" };
    }
  };

  const formatKey = (key: string) => {
    return key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
  };

  const columns: any[] = [
    {
      field: "name",
      headerName: "Full Name",
      flex: 1,
      renderCell: (params: any) => (
        <div>
          {params.row.user.name} {params.row.user.surname}
        </div>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => <div>{params.row.user.email}</div>,
    },
    {
      field: "submittedAt",
      headerName: "Submitted At",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) =>
        format(new Date(params.row.submittedAt || Date.now()), "dd/MM/yyyy"),
    },
    {
      field: "year",
      headerName: "Review Changes",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => handleRowClick(params)}
          style={{ boxShadow: "none", backgroundColor: "#6c757d" }}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <>
      <div className="form-content" style={{ justifyContent: "flex-end", marginRight: 240 }}>
        <TextField
          select
          label="Status"
          fullWidth
          size="small"
          value={statusFilter === null ? "" : statusFilter}
          onChange={(e) => setStatusFilter(e.target.value === "" ? null : Number(e.target.value))}
          style={{margin: 10, width: "20%"}}
        >
          <MenuItem value={0}>
            Pending
          </MenuItem>
          <MenuItem value={1}>
            Approved
          </MenuItem>
          <MenuItem value={2}>
            Rejected
          </MenuItem>
        </TextField>
      </div>

      <Box sx={{ height: 785, width: "70%", margin: "0 auto" }}>
        <DataGrid
          rows={workers}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          pageSizeOptions={[100]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 100 },
            },
          }}
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              fontSize: 15,
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#f0f0f0",
              fontWeight: "bold",
            },
            cursor: "pointer",
          }}
        />
      </Box>

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              overflow: "hidden",
            },
          }
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            color: "black",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: "bold" }}>
              Review Pending Changes
            </Typography>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{ color: "white" }}
            disabled={actionLoading}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {selectedRequest && (
            <Box>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: "background.default",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                      <PersonIcon color="action" fontSize="small" />
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        User:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                        {selectedRequest.user.name} {selectedRequest.user.surname}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                      <EmailIcon color="action" fontSize="small" />
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Email:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                        {selectedRequest.user.email}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                      <ScheduleIcon color="action" fontSize="small" />
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Submitted:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                        {format(new Date(selectedRequest.submittedAt), "dd/MM/yyyy HH:mm")}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>

              <Box sx={{ p: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold" }}>
                  Field Comparison
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider" }}>
                  <Table size="medium">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell width="30%"><strong>Field</strong></TableCell>
                        <TableCell width="35%"><strong>Current Data</strong></TableCell>
                        <TableCell width="35%"><strong>Requested Change</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const parsedChanges = getParsedChanges(selectedRequest.requestedChanges);

                        const isISODateString = (val: string) => {
                          return /^\d{4}-\d{2}-\d{2}/.test(val);
                        };

                        const normalizeForComparison = (val: any) => {
                          if (val === null || val === undefined) return "-";

                          if (typeof val === "string") {
                            const trimmed = val.trim();

                            if (trimmed === "") return "-";

                            if (isISODateString(trimmed)) {
                              return trimmed.split("T")[0];
                            }

                            return trimmed;
                          }

                          if (typeof val === "boolean") {
                            return val;
                          }
                          return String(val).trim();
                        };

                        const formatValue = (val: any) => {
                          if (val === null || val === undefined) return "—";

                          if (typeof val === "string") {
                            const trimmed = val.trim();
                          
                            if (trimmed === "") return "—";
                          
                            if (isISODateString(trimmed)) {
                              const date = new Date(trimmed);
                            
                              if (!isNaN(date.getTime())) {
                                return format(date, "dd/MM/yyyy");
                              }
                            }
                          
                            return trimmed;
                          }
                        
                          if (typeof val === "boolean") {
                            return val ? "Yes" : "No";
                          }

                          if (val instanceof Date) {
                            return format(val, "dd/MM/yyyy");
                          }

                          return String(val);
                        };

                        return Object.entries(parsedChanges).map(
                          ([key, requestedValue], index) => {
                            const userKey = Object.keys(selectedRequest.user).find(
                              (k) => k.toLowerCase() === key.toLowerCase()
                            );

                            const currentValue = userKey
                              ? selectedRequest.user[userKey]
                              : null;

                            const isChanged =
                              normalizeForComparison(currentValue) !==
                              normalizeForComparison(requestedValue);

                            const displayKey = formatKey(key);

                            return (
                              <TableRow
                                key={key}
                                sx={{
                                  bgcolor:
                                    index % 2 === 0
                                      ? "background.paper"
                                      : "action.hover",
                                  "&:hover": {
                                    bgcolor: "action.selected",
                                  },
                                }}
                              >
                                <TableCell component="th" scope="row">
                                  <Typography
                                    sx={{
                                      fontWeight: "medium",
                                      color: "primary.dark"
                                    }}>
                                    {displayKey}
                                  </Typography>
                                </TableCell>

                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "text.secondary" }}
                                  >
                                    {formatValue(currentValue)}
                                  </Typography>
                                </TableCell>

                                <TableCell
                                  sx={{
                                    transition: "background-color 0.2s",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    color={
                                      isChanged
                                        ? "error.dark"
                                        : "text.primary"
                                    }
                                    sx={{ fontWeight: isChanged ? "bold" : "normal" }}
                                  >
                                    {formatValue(requestedValue)}

                                    {isChanged && (
                                      <Chip
                                        label="Changed"
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        sx={{
                                          ml: 1,
                                          height: 20,
                                          fontSize: "0.7rem",
                                        }}
                                      />
                                    )}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          }
                        );
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>

                {Object.keys(getParsedChanges(selectedRequest.requestedChanges)).length === 0 && (
                  <Typography
                    align="center"
                    sx={{
                      color: "text.secondary",
                      mt: 2
                    }}>
                    No field changes requested.
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1, bgcolor: "background.paper" }}>
          <Button
            onClick={() => setReasonModalOpen(true)}
            color="error"
            variant="outlined"
            disabled={actionLoading}
            sx={{ textTransform: "none", fontWeight: "medium", width: '120px', boxShadow: "none" }}
          >
            Reject
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading && <CircularProgress size={20} />}
            sx={{ textTransform: "none", fontWeight: "medium", width: '120px', boxShadow: "none" }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reasonModalOpen}
        onClose={handleCloseReasonModal}
        fullWidth
        fullScreen={fullScreen}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              overflow: "hidden",
            },
          }
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            color: "black",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: "bold" }}>
              Reason
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {selectedRequest && (
            <Box>
              <Box sx={{ p: 3 }}>
                <TextField
                  label="Reason for Rejection"
                  multiline
                  rows={4}
                  fullWidth
                  variant="outlined"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: "background.paper" }}>
          <Button
            onClick={handleReject}
            variant="contained"
            disabled={actionLoading}
            sx={{ textTransform: "none", fontWeight: "medium", boxShadow: "none", width: '120px' }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PendingUpdates;
